// export const usePlaybackControl = () => {};

import { useState, useCallback, useRef } from "react";
import { IGif } from "services/types";
import { playbackTracker } from "utils/playback";

interface UseVideoPlayerProps {
  context: string | null;
  gif: IGif;
  videoRef: React.RefObject<HTMLVideoElement>;
  onSound: (sound: boolean) => void;
  onViewMetric: () => void;
}

const RETRY_DELAY = 1000;

export const usePlaybackControl = (props: UseVideoPlayerProps) => {
  const { gif, onSound, videoRef, context, onViewMetric } = props;
  const playPromiseRef = useRef<Promise<void>>();
  const [isTriedToPlay, setIsTriedToPlay] = useState<boolean>(false);
  const [paused, setPaused] = useState<boolean>(false);

  const handlePlayError = useCallback((e: Error) => {
    console.warn("failed to play video", e);
    onSound(false);

    setTimeout(() => {
      videoRef.current?.play().then(() => {
        setPaused(false);
        onViewMetric();
        playbackTracker.start(gif.id, context);
      }).catch(e => {
        console.error("Failed to play video on retry", e);
        setPaused(true);
        setIsTriedToPlay(true);
        playbackTracker.stop();
      });
    }, RETRY_DELAY);
  }, [gif, onViewMetric, videoRef, context, onSound]);

  const play = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    try {
      playPromiseRef.current = videoRef.current.play();

      if (playPromiseRef.current !== undefined) {
        playPromiseRef.current.then(() => {
          setPaused(false);
          onViewMetric();
          playbackTracker.start(gif.id, context);
        }).catch(handlePlayError).finally(() => {
          setIsTriedToPlay(true);
        });
      } else {
        setPaused(false);
        setIsTriedToPlay(true);
        onViewMetric();
        playbackTracker.start(gif.id, context);
      }
    } catch (e) {
      handlePlayError(e as Error);
    }
  }, [onViewMetric, gif, videoRef, handlePlayError, context]);

  const pause = useCallback(() => {
    if (playPromiseRef.current !== undefined) {
      playPromiseRef.current.then(() => {
        if (!videoRef.current) {
          return;
        }

        playbackTracker.stop();
        videoRef.current.pause();
        setPaused(true);
      });
    } else {
      if (!videoRef.current) {
        return;
      }

      playbackTracker.stop();
      videoRef.current.pause();
      setPaused(true);
    }
  }, [videoRef]);

  return { play, pause, paused, setPaused, isTriedToPlay };
};
